import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { EnvironmentOutlined } from "@ant-design/icons";
import { createStyles } from "antd-style";

import { JobLocationArrangment } from "@av/apps/recruitment/ats-common/components/JobLocationArrangment";
import { getLocalizedJobPostContent } from "@av/apps/recruitment/ats-common/localized-job-post-content";
import { jobsRoutes } from "@av/apps/recruitment/ats-common/routes/jobs/jobs.route-config";
import type { PublicJobPostDetailsModel } from "@av/apps/recruitment/ats-common/types";
import { Button } from "@av/components/atoms/Button";
import { List } from "@av/components/atoms/List";
import { Typography } from "@av/components/atoms/typography/Typography";
import { Link } from "@av/components/organisms/nav/Link";
import { useLocaleStore } from "@av/utils/hooks/useLocaleStore";

const { Text, Title } = Typography;
const useStyles = createStyles(({ token, css }) => ({
  jobItem: css`
    gap: ${token.marginSM}px;
    background: ${token.colorBgContainer};
    transition: all 0.2s ease;
    cursor: pointer;

    &:hover {
      border-color: ${token.colorPrimary};
      box-shadow: ${token.boxShadowSecondary};
    }
  `,
  selectedJob: css`
    border-color: ${token.colorPrimary};
    background: ${token["purple-2"]};
    box-shadow: ${token.boxShadowSecondary};
  `,
  jobContent: css`
    flex: 1 1 auto;
    min-width: 0;
  `,
  jobTitle: css`
    margin-bottom: ${token.marginXXS}px;
  `,
  jobTitleLink: css`
    display: inline;
    padding: 0;
    margin: 0;

    .ant-typography &,
    .ant-typography &:hover,
    .ant-typography &:focus,
    .ant-typography &:active {
      color: inherit;
    }
  `,
  jobId: css`
    display: block;
    margin-bottom: ${token.marginXXS}px;
  `,
  applyButton: css`
    flex-shrink: 0;
  `,
  locationIcon: css`
    color: ${token["blue-6"]};
    padding-right: ${token.marginXXS}px;
  `,
}));

interface JobListItemProps {
  job: PublicJobPostDetailsModel;
  testIdPrefix: string;
  isSelectedJob: boolean;
}

export function JobListItem({ job, testIdPrefix, isSelectedJob }: JobListItemProps) {
  const { t } = useTranslation();
  const { styles, cx } = useStyles();
  const jobTestIdPrefix = `${testIdPrefix}-job-${job.jobPostId}`;
  const navigate = useNavigate();
  const location = useLocation();
  const { locale } = useLocaleStore();
  const jobTitle = getLocalizedJobPostContent(job.jobTitles, locale);
  const isPosted = job.postStatus === "POSTED";
  const jobViewLocation = {
    pathname: jobsRoutes.home.view.path({ pathParam: job.jobPostId }),
    search: location.search,
  };

  return (
    <List.Item
      role={isPosted ? undefined : "link"}
      className={cx(styles.jobItem, {
        [styles.selectedJob]: isSelectedJob,
      })}
      onClick={() => navigate(jobViewLocation, { state: location.state })}
    >
      <div className={styles.jobContent}>
        <Title
          level={5}
          className={styles.jobTitle}
          ellipsis={{ tooltip: jobTitle }}
          testIdPrefix={`${jobTestIdPrefix}-position-name`}
        >
          {isPosted ? (
            <Link
              to={jobViewLocation}
              state={location.state}
              className={styles.jobTitleLink}
              onClick={(e) => e.stopPropagation()}
            >
              {jobTitle}
            </Link>
          ) : (
            jobTitle
          )}
        </Title>
        {job.jobId && (
          <Text type="secondary" className={styles.jobId} testIdPrefix={`${jobTestIdPrefix}-id`}>
            {t("recruitment.talentPool.jobId", { jobId: String(job.jobId) })}
          </Text>
        )}
        <Text testIdPrefix={`${jobTestIdPrefix}-location`}>
          <EnvironmentOutlined size={14} className={styles.locationIcon} />
          <JobLocationArrangment
            locationType={job.locationType}
            city={job.city}
            subdivisionCode={job.subdivisionCode}
            employmentType={job.employmentType}
          />
        </Text>
      </div>

      {isPosted && (
        <Button
          type="primary"
          className={styles.applyButton}
          testIdPrefix={`${jobTestIdPrefix}-apply`}
          onClick={(e) => {
            e.stopPropagation();
            navigate(jobsRoutes.home.apply.path({ pathParam: job.jobPostId }));
          }}
        >
          {t("recruitment.jobs.apply")}
        </Button>
      )}
    </List.Item>
  );
}
